import React, { useState, useEffect, useRef } from 'react';
import { Calendar, Clock, MapPin, Users, ChefHat, Star, Music, AlertCircle, Loader, Beer, Heart, Globe, Utensils, Search, Filter, DollarSign } from 'lucide-react';
import { supabase } from '../lib/supabase';
import { Link, useNavigate } from 'react-router-dom';
import { joinBlindDate } from '../lib/dinners';
import LoadingIndicator from '../components/LoadingIndicator';
import BackButton from '../components/BackButton';
import FilterSection from '../components/FilterSection';
import { atmosphereOptions, musicOptions, priceRangeOptions } from '../data/filterOptions';
import { useLanguage } from '../contexts/LanguageContext';
import { filterItems, getSearchableName, getEventFilterableProperties } from '../lib/filterUtils';

const BlindDates = () => {
  const navigate = useNavigate();
  const { t } = useLanguage();
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [joiningDate, setJoiningDate] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [hasVenue, setHasVenue] = useState(false);
  const [venue, setVenue] = useState<any>(null);
  const [venueType, setVenueType] = useState<'restaurant' | 'bar' | null>(null);
  const [blindDates, setBlindDates] = useState<any[]>([]);
  const [filteredBlindDates, setFilteredBlindDates] = useState<any[]>([]);
  const [animateCards, setAnimateCards] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedFilters, setSelectedFilters] = useState<string[]>([]);
  const [showFilters, setShowFilters] = useState(false);
  const searchInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    initialize();
  }, []);

  useEffect(() => {
    // Apply filtering whenever search query or filters change
    const filtered = filterItems(
      blindDates,
      searchQuery,
      selectedFilters,
      getSearchableName,
      getEventFilterableProperties
    );
    setFilteredBlindDates(filtered);
  }, [blindDates, searchQuery, selectedFilters]);

  useEffect(() => {
    // Trigger card animations after initial load
    const timer = setTimeout(() => {
      setAnimateCards(true);
    }, 100);
    return () => clearTimeout(timer);
  }, [filteredBlindDates]);

  const initialize = async () => {
    try {
      setLoading(true);
      setError(null);

      const { data: { session }, error: authError } = await supabase.auth.getSession();
      if (authError) throw authError;

      if (session?.user) {
        // Check venue ownership
        const { data: restaurant, error: restaurantError } = await supabase
          .from('restaurants')
          .select('*')
          .eq('owner_id', session.user.id)
          .maybeSingle();

        if (restaurantError && restaurantError.code !== 'PGRST116') {
          throw restaurantError;
        }

        const { data: bar, error: barError } = await supabase
          .from('bars')
          .select('*')
          .eq('owner_id', session.user.id)
          .maybeSingle();

        if (barError && barError.code !== 'PGRST116') {
          throw barError;
        }

        if (restaurant || bar) {
          setHasVenue(true);
          setVenue(restaurant || bar);
          setVenueType(restaurant ? 'restaurant' : 'bar');
        }
      }

      // Fetch blind dates
      await fetchBlindDates();
    } catch (err) {
      console.error('Error initializing:', err);
      setError(err instanceof Error ? err.message : 'Failed to initialize');
    } finally {
      setLoading(false);
    }
  };

  const fetchBlindDates = async () => {
    try {
      // Get current date and time
      const now = new Date();
      const currentDate = now.toISOString().split('T')[0];
      const currentTime = now.toTimeString().split(' ')[0];

      const { data: dates, error: datesError } = await supabase
        .from('blind_dates')
        .select(`
          *,
          restaurant:restaurants(*),
          bar:bars(*)
        `)
        .eq('status', 'open')
        .gt('spots_available', 0)
        .or(`date.gt.${currentDate},and(date.eq.${currentDate},time.gt.${currentTime})`)
        .order('date', { ascending: true })
        .order('time', { ascending: true });

      if (datesError) throw datesError;
      setBlindDates(dates || []);
      setFilteredBlindDates(dates || []);
    } catch (err) {
      console.error('Error fetching blind dates:', err);
      setError(err instanceof Error ? err.message : 'Failed to fetch blind dates');
    }
  };

  const handleJoinBlindDate = async (blindDateId: string) => {
    try {
      setJoiningDate(blindDateId);
      setError(null);
      
      await joinBlindDate(blindDateId);
      
      navigate('/my-dinners');
    } catch (err) {
      console.error('Error joining blind date:', err);
      setError(err instanceof Error ? err.message : 'Failed to join blind date');
      setJoiningDate(null);
    }
  };

  const handleFilterToggle = (filter: string) => {
    setSelectedFilters(prev => 
      prev.includes(filter) 
        ? prev.filter(f => f !== filter)
        : [...prev, filter]
    );
  };

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(e.target.value);
  };

  if (loading) {
    return <LoadingIndicator message={t('common.loading')} />;
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="bg-red-100 border border-red-400 text-red-700 px-6 py-4 rounded-lg flex items-center">
          <AlertCircle className="h-6 w-6 mr-3 flex-shrink-0" />
          <span>{error}</span>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <BackButton />
      {/* Hero Section */}
      <div className="relative mb-12 rounded-2xl overflow-hidden">
        <div 
          className="h-64 bg-cover bg-center"
          style={{
            backgroundImage: "url('https://images.unsplash.com/photo-1520671600189-56bdfdf526e3?q=80&w=2070&auto=format&fit=crop&ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D')"
          }}
        >
          <div className="absolute inset-0 bg-gradient-to-r from-black/70 to-black/50" />
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="text-center text-white">
              <h1 className="text-4xl md:text-5xl font-bold mb-4 transform transition-all duration-500 hover:scale-105">
                {t('blindDates.title')}
              </h1>
              <p className="text-xl text-gray-200 max-w-2xl mx-auto">
                {t('blindDates.subtitle')}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Search and Filters */}
      <div className="bg-white rounded-xl shadow-lg p-6 mb-8">
        <div className="flex flex-col md:flex-row gap-4 mb-6">
          <div className="flex-1 relative group">
            <div className="absolute inset-y-0 left-3 flex items-center pointer-events-none">
              <Search className="h-5 w-5 text-gray-400 group-hover:text-orange-500 transition-colors" />
            </div>
            <input
              ref={searchInputRef}
              type="text"
              placeholder={t('blindDates.searchPlaceholder')}
              className="pl-10 w-full p-3 border-2 border-gray-200 rounded-xl transition-all duration-300 focus:border-orange-500 focus:outline-none"
              value={searchQuery}
              onChange={handleSearchChange}
            />
          </div>
          <button
            onClick={() => setShowFilters(!showFilters)}
            className="flex items-center justify-center space-x-2 bg-orange-500 text-white px-6 py-3 rounded-xl hover:bg-orange-600 transition-all duration-300 transform hover:scale-105"
          >
            <Filter className="h-5 w-5" />
            <span>{showFilters ? t('search.hideFilters') : t('search.showFilters')}</span>
          </button>
        </div>

        {showFilters && (
          <div className="grid md:grid-cols-3 gap-6 pt-6 border-t animate-slideDown">
            <FilterSection
              title={t('blindDates.priceRange')}
              icon={DollarSign}
              options={priceRangeOptions}
              selectedFilters={selectedFilters}
              onFilterToggle={handleFilterToggle}
            />

            <FilterSection
              title={t('blindDates.music')}
              icon={Music}
              options={musicOptions}
              selectedFilters={selectedFilters}
              onFilterToggle={handleFilterToggle}
            />

            <FilterSection
              title={t('blindDates.atmosphere')}
              icon={Globe}
              options={atmosphereOptions}
              selectedFilters={selectedFilters}
              onFilterToggle={handleFilterToggle}
            />
          </div>
        )}
      </div>

      {/* Results Grid */}
      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
        {filteredBlindDates.map((date, index) => {
          const venue = date.restaurant || date.bar;
          const isBar = !!date.bar;
          
          return (
            <div
              key={date.id}
              className={`bg-white rounded-xl shadow-lg overflow-hidden transform transition-all duration-500 hover:shadow-2xl hover:-translate-y-1 ${
                animateCards ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
              }`}
              style={{ transitionDelay: `${index * 100}ms` }}
            >
              {venue.pictures?.exterior?.[0] ? (
                <div className="relative h-48 overflow-hidden group">
                  <img
                    src={venue.pictures.exterior[0]}
                    alt={venue.name}
                    className="w-full h-full object-cover transform transition-transform duration-500 group-hover:scale-110"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
                </div>
              ) : (
                <div className="h-48 bg-gray-200 flex items-center justify-center">
                  {isBar ? (
                    <Beer className="h-12 w-12 text-gray-400" />
                  ) : (
                    <ChefHat className="h-12 w-12 text-gray-400" />
                  )}
                </div>
              )}

              <div className="p-6">
                <div className="flex justify-between items-start mb-4">
                  <div>
                    <h3 className="text-xl font-semibold text-gray-900">
                      {venue.name}
                    </h3>
                    <div className="flex items-center space-x-2 text-gray-600">
                      {isBar ? (
                        <>
                          <Beer className="h-4 w-4" />
                          <span>{venue.bar_type}</span>
                        </>
                      ) : (
                        <>
                          <ChefHat className="h-4 w-4" />
                          <span>{venue.cuisine_type}</span>
                        </>
                      )}
                      <span>•</span>
                      <span>{date.price_range}</span>
                    </div>
                  </div>
                  <div className="flex items-center space-x-1 bg-yellow-100 px-2 py-1 rounded-full">
                    <Star className="h-4 w-4 text-yellow-500 fill-current" />
                    <span className="text-yellow-700 font-medium">
                      {venue.rating || '4.5'} ({venue.reviews_count || '0'})
                    </span>
                  </div>
                </div>

                <div className="space-y-2 mb-4">
                  <div className="flex items-center space-x-2 text-gray-600">
                    <Calendar className="h-4 w-4" />
                    <span>{new Date(date.date).toLocaleDateString()}</span>
                  </div>
                  <div className="flex items-center space-x-2 text-gray-600">
                    <Clock className="h-4 w-4" />
                    <span>{date.time.slice(0, 5)}</span>
                  </div>
                  <div className="flex items-center space-x-2 text-gray-600">
                    <MapPin className="h-4 w-4" />
                    <span>{venue.address}</span>
                  </div>
                  <div className="flex items-center space-x-2 text-gray-600">
                    <Users className="h-4 w-4" />
                    <span>{date.spots_available}/{date.spots_total} {t('common.spotsAvailable')}</span>
                  </div>
                  {venue.features?.live_music && (
                    <div className="flex items-center space-x-2 text-gray-600">
                      <Music className="h-4 w-4" />
                      <span>{t('blindDates.liveMusic')}</span>
                    </div>
                  )}
                </div>

                <button
                  onClick={() => handleJoinBlindDate(date.id)}
                  disabled={joiningDate === date.id}
                  className="w-full bg-orange-500 text-white px-4 py-3 rounded-xl hover:bg-orange-600 transition-all duration-300 transform hover:scale-105 flex items-center justify-center space-x-2 disabled:opacity-75 disabled:cursor-wait focus:outline-none focus:ring-2 focus:ring-orange-500 focus:ring-offset-2"
                >
                  {joiningDate === date.id ? (
                    <>
                      <div className="h-5 w-5 animate-spin" />
                      <span>{t('blindDates.joining')}</span>
                    </>
                  ) : (
                    <>
                      <Heart className="h-5 w-5" />
                      <span>{t('blindDates.join')}</span>
                    </>
                  )}
                </button>
              </div>
            </div>
          );
        })}
      </div>

      {filteredBlindDates.length === 0 && (
        <div className="bg-white rounded-xl shadow-lg p-12 text-center transform transition-all duration-500 hover:shadow-xl">
          <Heart className="h-16 w-16 text-gray-400 mx-auto mb-6" />
          <h3 className="text-xl font-medium text-gray-900 mb-4">{t('blindDates.noResults')}</h3>
          <p className="text-gray-600 mb-8">
            {t('blindDates.checkBack')}
          </p>
          <Link
            to="/create-dinner"
            className="inline-flex items-center justify-center space-x-2 bg-orange-500 text-white px-6 py-3 rounded-xl hover:bg-orange-600 transition-all duration-300 transform hover:scale-105"
          >
            <Heart className="h-5 w-5" />
            <span>{t('blindDates.hostDate')}</span>
          </Link>
        </div>
      )}
    </div>
  );
};

export default BlindDates;